/**
 * Модель для работы с персоналом
 */

import { createModel } from '@rematch/core';
import { RootModel } from './index';

import {
  IArea,
  IDepartments,
  IEmployeePresence,
  IEmployeesList,
  IOffice,
  IPost,
  IRole,
  ISelect,
  IUserEmployees,
  PresenceStatus
} from './types';

import UsersService, { IUpdateOrCreateBody } from '../services/UsersService';
import ProtectionsService from '../services/ProtectionsService';
import MapsService from '../services/MapsService';
import { saveAs } from 'file-saver';
import moment from 'moment';
import { pageSize as defaultPageSize } from '../constants/pagination';
import {
  filterEmployeesByPresenceStatus,
  mergeEmployeesWithPresence,
  paginateList,
  sameEmployeeFilters
} from '../utils/employeePresence';

export interface IData {
  roles: IRole[];
  posts: IPost[];
  protections: ISelect[];
  departments: IDepartments[];
  offices: IOffice[];
  areas: IArea[];
}

interface IList {
  data: IEmployeesList[] | null;
  pagesCount: number;
  lastListParams?: EmployeeListParams | null;
  presenceFailed?: boolean;
  /** Full HR roster for current non-presence filters (used when presenceStatus is set). */
  rosterAll?: IEmployeesList[] | null;
  /** Presence-filtered subset of rosterAll. */
  filteredAll?: IEmployeesList[] | null;
}

interface IState {
  data: IData;
  list: IList;
  item: IUserEmployees | null;
  importResult: any;
}

const initialState: IState = {
  data: {
    roles: [],
    posts: [],
    protections: [],
    departments: [],
    offices: [],
    areas: []
  },
  list: {
    data: null,
    pagesCount: 0,
    lastListParams: null,
    presenceFailed: false,
    rosterAll: null,
    filteredAll: null
  },
  item: null,
  importResult: null
};

type EmployeeListParams = {
  presenceStatus?: PresenceStatus | string;
  pageIndex?: number;
  pageSize?: number;
  fullTextSearch?: string;
  departmentId?: string;
  device?: string;
  lastPosition?: string;
  roles?: string;
  posts?: string;
  includePreview?: boolean;
};

/** Invalidates in-flight list loads; refresh skips while a load is running. */
let listRequestSeq = 0;
let listInFlight = false;

async function fetchPresenceRows(
  options: { users?: (string | number)[]; fts?: string; serialNumber?: string } = {}
): Promise<{ rows: IEmployeePresence[]; failed: boolean }> {
  try {
    const { data: presence } = await UsersService.getEmployeesPresence({
      scope: 'ALL',
      ...(options.users?.length ? { users: options.users } : {}),
      ...(options.fts ? { fts: options.fts } : {}),
      ...(options.serialNumber ? { serialNumber: options.serialNumber } : {})
    });
    return { rows: presence?.data || [], failed: false };
  } catch {
    return { rows: [], failed: true };
  }
}

async function fetchAllEmployees(
  employeeParams: Omit<EmployeeListParams, 'presenceStatus' | 'pageIndex'>
): Promise<IEmployeesList[]> {
  let pageIndex = 0;
  let pagesCount = 1;
  const all: IEmployeesList[] = [];

  while (pageIndex < pagesCount) {
    const { data } = await UsersService.getAllEmployees({ ...employeeParams, pageIndex });
    all.push(...(data.employees || []));
    pagesCount = data.pagesCount ?? 1;
    pageIndex += 1;
  }

  return all;
}

function presenceSearchOptions(fullTextSearch?: string) {
  const q = fullTextSearch || undefined;
  if (!q) {
    return {};
  }
  // Pass both: Indoor fts covers names; serialNumber covers tracker numbers (same as employees FTS).
  return { fts: q, serialNumber: q };
}

export const staffUsers = createModel<RootModel>()({
  state: initialState,
  reducers: {
    setData(state: IState, payload: IData): IState {
      return { ...state, data: { ...state.data, ...payload } };
    },
    setList(state: IState, payload: IList): IState {
      return { ...state, list: payload };
    },
    patchList(state: IState, payload: Partial<IList>): IState {
      return { ...state, list: { ...state.list, ...payload } };
    },
    setItem(state: IState, payload: IUserEmployees): IState {
      return { ...state, item: payload };
    },
    setImportResult(state: IState, payload: any): IState {
      return { ...state, importResult: payload };
    },
    reset(): IState {
      listRequestSeq += 1;
      listInFlight = false;
      return { ...initialState };
    }
  },
  effects: () => ({
    async getData() {
      try {
        const [
          { data: roles },
          { data: posts },
          {
            data: { data: protections }
          },
          { data: departments },
          {
            data: { data: offices }
          },
          { data: areas }
        ] = await Promise.all([
          UsersService.getRoles(),
          UsersService.getPosts(),
          ProtectionsService.getTypeAll({ pageSize: 100 }),
          UsersService.getDepartmentsAll({ includeEmptyDepartment: true }),
          MapsService.getOffices(),
          MapsService.getAreas()
        ]);
        this.setData({ roles, posts, protections, departments, offices, areas });
      } catch (error) {
        return error;
      }
    },
    async getList(params: EmployeeListParams = {}, rootState?: any) {
      const requestId = ++listRequestSeq;
      listInFlight = true;

      try {
        const {
          presenceStatus,
          pageIndex = 0,
          fullTextSearch,
          departmentId,
          device,
          lastPosition,
          roles,
          posts,
          includePreview
        } = params;

        const employeeParams = {
          fullTextSearch,
          departmentId,
          device,
          lastPosition,
          roles,
          posts,
          includePreview
        };

        const prevList: IList = rootState?.staffUsers?.list || initialState.list;

        // Presence filter + only page changed → paginate cached roster (no network).
        if (
          presenceStatus &&
          prevList.filteredAll &&
          sameEmployeeFilters(prevList.lastListParams, params)
        ) {
          const { page, pagesCount } = paginateList(
            prevList.filteredAll,
            pageIndex,
            defaultPageSize
          );
          if (requestId !== listRequestSeq) {
            return;
          }
          this.setList({
            data: page,
            pagesCount,
            lastListParams: params,
            presenceFailed: prevList.presenceFailed ?? false,
            rosterAll: prevList.rosterAll ?? null,
            filteredAll: prevList.filteredAll
          });
          return;
        }

        if (presenceStatus) {
          const { rows: presenceRows, failed: presenceFailed } = await fetchPresenceRows(
            presenceSearchOptions(fullTextSearch)
          );

          if (requestId !== listRequestSeq) {
            return;
          }

          if (presenceFailed) {
            // Keep previous filtered view if same filters; never silently drop the filter.
            if (
              prevList.filteredAll &&
              sameEmployeeFilters(prevList.lastListParams, params)
            ) {
              const { page, pagesCount } = paginateList(
                prevList.filteredAll,
                pageIndex,
                defaultPageSize
              );
              this.setList({
                data: page,
                pagesCount,
                lastListParams: params,
                presenceFailed: true,
                rosterAll: prevList.rosterAll ?? null,
                filteredAll: prevList.filteredAll
              });
              return;
            }

            this.setList({
              data: [],
              pagesCount: 0,
              lastListParams: params,
              presenceFailed: true,
              rosterAll: [],
              filteredAll: []
            });
            return;
          }

          const reuseRoster =
            prevList.rosterAll &&
            sameEmployeeFilters(
              { ...prevList.lastListParams, presenceStatus: undefined },
              { ...params, presenceStatus: undefined }
            );

          const rosterAll = reuseRoster
            ? prevList.rosterAll!
            : await fetchAllEmployees(employeeParams);

          if (requestId !== listRequestSeq) {
            return;
          }

          const matchingIds = new Set(
            presenceRows
              .filter(row => row.presenceStatus === presenceStatus)
              .map(row => String(row.userId))
          );

          const matchedEmployees = rosterAll.filter(employee =>
            matchingIds.has(String(employee.id))
          );
          const merged = mergeEmployeesWithPresence(matchedEmployees, presenceRows);
          const filteredAll = filterEmployeesByPresenceStatus(merged, presenceStatus);
          const { page, pagesCount } = paginateList(filteredAll, pageIndex, defaultPageSize);

          this.setList({
            data: page,
            pagesCount,
            lastListParams: params,
            presenceFailed: false,
            rosterAll,
            filteredAll
          });
          return;
        }

        const { data } = await UsersService.getAllEmployees({ ...employeeParams, pageIndex });
        if (requestId !== listRequestSeq) {
          return;
        }

        const employees: IEmployeesList[] = data.employees || [];

        const { rows: presenceRows, failed: presenceFailed } =
          employees.length > 0
            ? await fetchPresenceRows({
                users: employees.map(employee => employee.id)
              })
            : { rows: [], failed: false };

        if (requestId !== listRequestSeq) {
          return;
        }

        const merged = mergeEmployeesWithPresence(employees, presenceRows, { presenceFailed });

        this.setList({
          data: merged,
          pagesCount: data.pagesCount ?? 0,
          lastListParams: params,
          presenceFailed,
          rosterAll: null,
          filteredAll: null
        });
      } catch (error) {
        if (requestId === listRequestSeq) {
          return error;
        }
      } finally {
        if (requestId === listRequestSeq) {
          listInFlight = false;
        }
      }
    },

    async refreshPresence(_payload: void, rootState?: any) {
      if (listInFlight) {
        return;
      }

      const list: IList | undefined = rootState?.staffUsers?.list;
      const lastListParams = list?.lastListParams;
      if (!list || !lastListParams) {
        return;
      }

      const requestId = listRequestSeq;
      const pageIndex = lastListParams.pageIndex ?? 0;
      const presenceStatus = lastListParams.presenceStatus;
      const search = presenceSearchOptions(lastListParams.fullTextSearch);

      // Presence-filtered mode: refresh presence only, re-filter cached roster.
      if (presenceStatus && list.rosterAll) {
        const { rows: presenceRows, failed: presenceFailed } = await fetchPresenceRows(search);

        if (requestId !== listRequestSeq || listInFlight) {
          return;
        }

        if (presenceFailed) {
          this.patchList({ presenceFailed: true });
          return;
        }

        const matchingIds = new Set(
          presenceRows
            .filter(row => row.presenceStatus === presenceStatus)
            .map(row => String(row.userId))
        );
        const matchedEmployees = list.rosterAll.filter(employee =>
          matchingIds.has(String(employee.id))
        );
        const merged = mergeEmployeesWithPresence(matchedEmployees, presenceRows);
        const filteredAll = filterEmployeesByPresenceStatus(merged, presenceStatus);
        const { page, pagesCount } = paginateList(filteredAll, pageIndex, defaultPageSize);

        this.setList({
          data: page,
          pagesCount,
          lastListParams,
          presenceFailed: false,
          rosterAll: list.rosterAll,
          filteredAll
        });
        return;
      }

      // Default page mode: refresh presence for currently visible rows only.
      const current = list.data || [];
      if (!current.length) {
        return;
      }

      const { rows: presenceRows, failed: presenceFailed } = await fetchPresenceRows({
        users: current.map(employee => employee.id)
      });

      if (requestId !== listRequestSeq || listInFlight) {
        return;
      }

      if (presenceFailed) {
        this.patchList({ presenceFailed: true });
        return;
      }

      this.setList({
        data: mergeEmployeesWithPresence(current, presenceRows),
        pagesCount: list.pagesCount,
        lastListParams,
        presenceFailed: false,
        rosterAll: null,
        filteredAll: null
      });
    },

    async exportList(params: EmployeeListParams = {}) {
      try {
        if (params.presenceStatus) {
          // Export API has no presence filter — refuse rather than export a mismatched set.
          return;
        }
        const { presenceStatus: _presenceStatus, ...exportParams } = params;
        const { data } = await UsersService.getCsvAllEmployees(exportParams);
        saveAs(data, `Персонал ${moment().format('DD.MM.YYYY HH-mm-ss')}.csv`);
      } catch (error) {
        return error;
      }
    },

    async importList(body: File | null) {
      const doc = new FormData();
      if (body) {
        doc.append('file', body);
      }
      try {
        const { data } = await UsersService.importFromFileUserList(doc);
        this.setImportResult(data);
      } catch (error: any) {
        const dataToJSON = await error.response.data
          .text()
          .then((data: string) => JSON.parse(data));
        return {
          ...error,
          response: { data: dataToJSON }
        };
      }
    },
    async getItem(id: string) {
      const { data } = await UsersService.getEmployees(id);

      this.setItem(data);
    },
    async updateOrCreate(body: IUpdateOrCreateBody) {
      try {
        await UsersService.updateOrCreateEmployees(body);
      } catch (error) {
        return error;
      }
    },
    async remove(id) {
      try {
        await UsersService.deleteEmployees(id);
      } catch (error) {
        return error;
      }
    }
  })
});
